import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactPlayer from 'react-player';

import RyosSeekBar from './RyosSeekBar';
import { useRyosVideosPlayer, formatRyosTime } from './useRyosVideosPlayer';

/* ── dimensions ─────────────────────────────────────────────────── */
const VP_WIDTH = 480;
const VP_HEIGHT = 420;

/* ── white-noise canvas ─────────────────────────────────────────── */
function WhiteNoiseEffect({ active }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const brightnessRef = useRef(0);
  const brightAnimRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
      brightnessRef.current = 0;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    let imageData = null;
    let pixels32 = null;
    const resize = () => {
      const w = Math.max(1, Math.floor(canvas.offsetWidth / 1.5));
      const h = Math.max(1, Math.floor(canvas.offsetHeight / 1.5));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        imageData = null;
        pixels32 = null;
      }
      if (!imageData || !pixels32) {
        imageData = ctx.createImageData(w, h);
        pixels32 = new Uint32Array(imageData.data.buffer);
      }
    };
    const FRAME_INTERVAL = 1000 / 15; // ~15 fps
    let lastFrameTime = 0;
    const draw = (now) => {
      if (!imageData || !pixels32) {
        animFrameRef.current = requestAnimationFrame(draw);
        return;
      }
      if (now - lastFrameTime < FRAME_INTERVAL) {
        animFrameRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = now;
      const b = brightnessRef.current;
      const len = pixels32.length;
      for (let i = 0; i < len; i++) {
        const v = (Math.random() * 255 * b) | 0;
        pixels32[i] = 0xff000000 | (v << 16) | (v << 8) | v;
      }
      for (let y = 0; y < canvas.height; y += 2) {
        const rs = y * canvas.width;
        const re = rs + canvas.width;
        for (let i = rs; i < re; i++) {
          const v = ((pixels32[i] & 0xff) * 205) >> 8;
          pixels32[i] = 0xff000000 | (v << 16) | (v << 8) | v;
        }
      }
      ctx.putImageData(imageData, 0, 0);
      animFrameRef.current = requestAnimationFrame(draw);
    };
    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
      imageData = null;
      pixels32 = null;
    };
  }, [active]);

  // brightness fade-in
  useEffect(() => {
    if (!active) {
      if (brightAnimRef.current !== null) cancelAnimationFrame(brightAnimRef.current);
      brightAnimRef.current = null;
      brightnessRef.current = 0;
      return;
    }
    const dur = 1000;
    const t0 = performance.now();
    const anim = now => {
      const p = Math.min((now - t0) / dur, 1);
      brightnessRef.current = 1 - Math.pow(1 - p, 3);
      if (p < 1) brightAnimRef.current = requestAnimationFrame(anim);
      else brightAnimRef.current = null;
    };
    brightAnimRef.current = requestAnimationFrame(anim);
    return () => {
      if (brightAnimRef.current !== null) cancelAnimationFrame(brightAnimRef.current);
      brightAnimRef.current = null;
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0,
        width: 'calc(100% + 1px)',
        height: 'calc(100% + 1px)',
        pointerEvents: 'none',
      }}
    />
  );
}

/* ── animated digit (ryos flip counter) ─────────────────────────── */
function AnimatedDigit({ digit, direction }) {
  const y = direction === 'next' ? 30 : -30;
  return (
    <DigitSlot>
      <AnimatePresence initial={false}>
        <DigitMotion
          key={digit}
          initial={{ y, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y, opacity: 0 }}
          transition={{
            y: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          {digit}
        </DigitMotion>
      </AnimatePresence>
    </DigitSlot>
  );
}

function AnimatedNumber({ number }) {
  const [prev, setPrev] = useState(number);
  const dir = number > prev ? 'next' : 'prev';
  useEffect(() => setPrev(number), [number]);
  const digits = String(number).padStart(2, '0').split('');
  return (
    <DigitRow>
      {digits.map((d, i) => (
        <AnimatedDigit key={i} digit={d} direction={dir} />
      ))}
    </DigitRow>
  );
}

/* ── marquee title ──────────────────────────────────────────────── */
const marqueeScroll = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-100%); }
`;

function AnimatedTitle({ title, isPlaying }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const c = containerRef.current;
    const m = measureRef.current;
    if (!c || !m) return;
    const check = () => setOverflows(m.scrollWidth > c.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(c);
    return () => ro.disconnect();
  }, [title]);

  const showFade = overflows && !isPlaying;

  return (
    <TitleContainer ref={containerRef} $fade={showFade}>
      <TitleMeasure ref={measureRef} aria-hidden>{title}</TitleMeasure>
      <TitleTrack>
        <TitleText $playing={isPlaying} $animate={isPlaying}>{title}</TitleText>
        {isPlaying && (
          <TitleText $playing $animate aria-hidden>{title}</TitleText>
        )}
      </TitleTrack>
    </TitleContainer>
  );
}

/* ── status overlay ─────────────────────────────────────────────── */
function StatusDisplay({ message }) {
  return (
    <StatusWrap>
      <StatusShadow>{message}</StatusShadow>
      <StatusFg>{message}</StatusFg>
    </StatusWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
function WindowsMediaPlayer() {
  const v = useRyosVideosPlayer();
  const shouldShowWhiteNoise = !v.isPlaying;

  return (
    <Root>
      {/* ── VIDEO AREA ── */}
      <VideoArea>
        <VideoInner
          onMouseEnter={() => v.setIsVideoHovered(true)}
          onMouseLeave={() => v.setIsVideoHovered(false)}
        >
          {/* oversized wrapper to hide YouTube chrome — kept for parity */}
          <VideoOversized>
            <PlayerWrap>
              <ReactPlayer
                ref={v.playerRef}
                url={v.videoUrl}
                playing={v.isPlaying}
                playsinline
                controls={false}
                width="100%"
                height="100%"
                progressInterval={500}
                onProgress={v.handleProgress}
                onDuration={v.handleDuration}
                onEnded={v.handleVideoEnd}
                onPlay={() => v.setIsPlaying(true)}
                onPause={() => v.setIsPlaying(false)}
                config={{
                  file: {
                    attributes: {
                      playsInline: true,
                      'webkit-playsinline': 'true',
                      preload: 'auto',
                      loop: false,
                    },
                  },
                }}
              />
            </PlayerWrap>

            {/* white noise overlay */}
            <AnimatePresence>
              {shouldShowWhiteNoise && (
                <NoiseMotion
                  initial={{ opacity: 0, scale: 1.15 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.15 }}
                  transition={{ duration: 0.2, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
                >
                  <WhiteNoiseEffect active />
                </NoiseMotion>
              )}
            </AnimatePresence>

            {/* click-to-play overlay */}
            <ClickOverlay
              aria-label={v.isPlaying ? 'Pause' : 'Play'}
              onPointerDown={v.handleOverlayPointerDown}
              onPointerMove={v.handleOverlayPointerMove}
              onPointerUp={v.handleOverlayPointerUp}
              onPointerCancel={v.handleOverlayPointerCancel}
            />
          </VideoOversized>

          {/* seek bar */}
          <SeekBarLayer>
            <RyosSeekBar
              duration={v.duration}
              currentTime={v.playedSeconds}
              onSeek={v.handleSeek}
              isPlaying={v.isPlaying}
              isHovered={v.isVideoHovered}
              onDragChange={v.onDragSeekChange}
            />
          </SeekBarLayer>

          {/* status toast */}
          <AnimatePresence>
            {v.statusMessage && (
              <StatusMotion
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <StatusDisplay message={v.statusMessage} />
              </StatusMotion>
            )}
          </AnimatePresence>
        </VideoInner>
      </VideoArea>

      {/* ── CONTROLS AREA ── */}
      <ControlsArea>
        {/* LCD display */}
        <LCD>
          <LCDGroup>
            <LCDLabel $active={v.isPlaying}>TRACK</LCDLabel>
            <LCDValue $active={v.isPlaying}>
              <AnimatedNumber number={v.trackNumber} />
            </LCDValue>
          </LCDGroup>
          <LCDGroup>
            <LCDLabel $active={v.isPlaying}>TIME</LCDLabel>
            <LCDValue $active={v.isPlaying}>
              {formatRyosTime(v.isDraggingSeek ? Math.floor(v.dragSeekTime) : v.elapsedTime)}
            </LCDValue>
          </LCDGroup>
          <LCDTitleArea>
            <LCDLabel $active={v.isPlaying} style={{ paddingLeft: 8 }}>TITLE</LCDLabel>
            <AnimatedTitle title={v.videoTitle} isPlaying={v.isPlaying} />
          </LCDTitleArea>
        </LCD>

        {/* transport buttons */}
        <Transport>
          <TransportBtnGroup>
            <TransportBtn onClick={v.previousVideo} aria-label="Previous">
              ⏮
            </TransportBtn>
            <TransportBtn $wide onClick={v.togglePlay} aria-label={v.isPlaying ? 'Pause' : 'Play'}>
              {v.isPlaying ? '⏸' : '▶'}
            </TransportBtn>
            <TransportBtn onClick={v.nextVideo} aria-label="Next">
              ⏭
            </TransportBtn>
          </TransportBtnGroup>
        </Transport>
      </ControlsArea>
    </Root>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════════════════════════ */
const MAGENTA = '#ff00ff';
const MUTED = '#666';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  color: #fff;
  overflow: hidden;
`;

/* ── video ───────────────────────────────────────────────────────── */
const VideoArea = styled.div`
  flex: 1 1 0;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.55);
`;

const VideoInner = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const VideoOversized = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const PlayerWrap = styled.div`
  position: absolute;
  inset: 0;
  & video {
    object-fit: contain;
    width: 100% !important;
    height: 100% !important;
    background: #000;
  }
`;

const NoiseMotion = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 10;
`;

const ClickOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  cursor: pointer;
`;

const SeekBarLayer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 30;
`;

const StatusMotion = styled(motion.div)`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 40;
`;

const StatusWrap = styled.div`
  position: relative;
`;
const StatusFg = styled.div`
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #fff;
  position: relative;
  z-index: 1;
`;
const StatusShadow = styled.div`
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #000;
  position: absolute;
  inset: 0;
  -webkit-text-stroke: 3px #000;
`;

/* ── controls ────────────────────────────────────────────────────── */
const ControlsArea = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  background: #2a2a2a;
  border-top: 1px solid #3a3a3a;
`;

const LCD = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  background: #000;
  padding: 8px 16px;
  border-radius: 2px;
`;

const LCDGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const LCDLabel = styled.div`
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 9px;
  letter-spacing: 0.5px;
  transition: color 0.3s;
  color: ${({ $active }) => ($active ? MAGENTA : MUTED)};
`;

const LCDValue = styled.div`
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.4;
  transition: color 0.3s;
  color: ${({ $active }) => ($active ? MAGENTA : MUTED)};
`;

const LCDTitleArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

/* ── animated title ──────────────────────────────────────────────── */
const TitleContainer = styled.div`
  position: relative;
  height: 22px;
  margin-bottom: 3px;
  overflow: hidden;
  ${({ $fade }) =>
    $fade &&
    css`
      mask-image: linear-gradient(to right, black calc(100% - 32px), transparent);
      -webkit-mask-image: linear-gradient(to right, black calc(100% - 32px), transparent);
    `}
`;
const TitleMeasure = styled.span`
  position: absolute;
  visibility: hidden;
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 18px;
  padding: 0 8px;
  white-space: nowrap;
  pointer-events: none;
`;
const TitleTrack = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  white-space: nowrap;
`;
const TitleText = styled.div`
  flex-shrink: 0;
  font-family: 'Geneva', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 18px;
  padding: 0 8px;
  margin-top: -2px;
  transition: color 0.3s;
  color: ${({ $playing }) => ($playing ? MAGENTA : MUTED)};
  opacity: ${({ $playing }) => ($playing ? 1 : 0.5)};
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${marqueeScroll} 20s linear infinite;
    `}
`;

/* ── transport buttons ───────────────────────────────────────────── */
const Transport = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TransportBtnGroup = styled.div`
  display: flex;
  gap: 0;
`;

const TransportBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $wide }) => ($wide ? '50px' : '36px')};
  height: 26px;
  border: 1px solid #555;
  border-left: ${({ $wide }) => ($wide ? 'none' : '1px solid #555')};
  border-right: ${({ $wide }) => ($wide ? 'none' : '1px solid #555')};
  border-radius: 0;
  background: linear-gradient(180deg, #3a3a3a 0%, #2a2a2a 100%);
  color: #ccc;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  &:first-child { border-radius: 2px 0 0 2px; border-right: none; }
  &:last-child  { border-radius: 0 2px 2px 0; border-left: none; }
  &:hover { filter: brightness(1.2); }
  &:active { filter: brightness(0.8); }
`;

/* ── animated digit slots ────────────────────────────────────────── */
const DigitSlot = styled.div`
  position: relative;
  width: 0.6em;
  height: 28px;
  overflow: hidden;
  display: inline-block;
`;
const DigitMotion = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
`;
const DigitRow = styled.div`
  display: flex;
`;

export { VP_HEIGHT, VP_WIDTH };
export default WindowsMediaPlayer;
